import { createClient } from '@supabase/supabase-js';

const url = process.env.REACT_APP_SUPABASE_URL;
const anon = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(url, anon, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: 'velvet-os-auth',
  },
});

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Read the access token synchronously from localStorage.
// We intentionally bypass supabase.auth.getSession() here because its internal
// auth lock can deadlock when the token-refresh path times out in flaky networks,
// which then permanently strands every .from().select() call AND every apiFetch.
function readTokenSync() {
  try {
    const raw = localStorage.getItem('velvet-os-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const s = parsed?.currentSession || parsed?.session || parsed;
    if (!s?.access_token) return null;
    const expiresAt = s.expires_at || s.expiresAt;
    if (expiresAt && Number(expiresAt) * 1000 < Date.now()) return null;
    return s.access_token;
  } catch { return null; }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Race a promise against a hard timeout — critical for preview-env flakiness.
export function withTimeout(promise, ms, label = 'op') {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
  ]);
}

// 3 attempts by default, 400/800ms backoff. Same shape used in AuthContext.
export async function withRetry(fn, { attempts = 3, label = 'op', backoff = 400 } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn(i);
    } catch (e) {
      lastErr = e;
      // eslint-disable-next-line no-console
      console.warn(`[data] ${label} attempt ${i + 1}/${attempts} failed:`, e?.message || e);
      if (i < attempts - 1) await sleep(backoff * (i + 1));
    }
  }
  throw lastErr;
}

/**
 * Fetch a backend /api/* endpoint.
 * Adds a fresh access token from localStorage on each call (no supabase-js lock)
 * plus a 12s hard timeout, and lets callers layer withRetry on top.
 */
export async function apiFetch(path, options = {}) {
  const token = readTokenSync();
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const timeoutMs = options.timeoutMs || 12000;
  const res = await withTimeout(fetch(`${API}${path}`, { ...options, headers }), timeoutMs, `fetch ${path}`);
  const text = await res.text();
  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  let json = null;
  if (isJson && text) {
    try { json = JSON.parse(text); } catch { /* fall through */ }
  }
  if (!res.ok) {
    // When the response is HTML (Cloudflare/edge error page, gateway timeout, etc.)
    // never surface the entire HTML to the user — show a compact, translated hint.
    let msg;
    if (json?.detail || json?.error) {
      msg = json.detail || json.error;
    } else if (!isJson) {
      if (res.status === 502) msg = 'El proxy respondió con 502 (posible tiempo agotado en la sincronización). Intenta de nuevo; el checkpoint no avanzó.';
      else if (res.status === 504) msg = 'Tiempo de espera agotado en el servidor. Intenta de nuevo.';
      else msg = `HTTP ${res.status} — respuesta no JSON del servidor.`;
    } else {
      msg = text || `HTTP ${res.status}`;
    }
    const err = new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    err.status = res.status;
    err.data = json;
    throw err;
  }
  return json;
}
